import { useMemo, useState } from 'react'
import { useSuspenseQuery } from '@tanstack/react-query'
import { createFileRoute, Link } from '@tanstack/react-router'
import type { ColumnDef } from '@tanstack/react-table'
import { LayersIcon, PencilIcon, PlusIcon } from 'lucide-react'

import { Button } from '@ems/ui/components/button'
import { DataTable } from '@ems/ui/patterns/data-table'
import { EmptyState } from '@ems/ui/patterns/empty-state'
import { PageHeader } from '@ems/ui/patterns/page-header'

import { sessionQuery } from '#admin/features/auth/queries.ts'
import { hasPlatformScope } from '#admin/features/platform/api.ts'
import type { Series } from '#admin/features/platform/series/api.ts'
import { seriesQuery } from '#admin/features/platform/series/queries.ts'
import { SeriesDialog } from '#admin/features/platform/series/series-dialog.tsx'
import { SeriesSkeleton } from '#admin/features/platform/platform-skeletons.tsx'

/** `/platform/series` — the brands editions belong to. */
export const Route = createFileRoute('/_authenticated/platform/series/')({
  pendingComponent: SeriesSkeleton,
  loader: ({ context }) => context.queryClient.ensureQueryData(seriesQuery()),
  component: SeriesPage,
})

function SeriesPage() {
  const { data } = useSuspenseQuery(seriesQuery())
  const { data: session } = useSuspenseQuery(sessionQuery())
  const canManage = hasPlatformScope(session, 'platform:tenants.manage')
  const [dialog, setDialog] = useState<{ editing: Series | null } | null>(null)

  const columns = useMemo<ColumnDef<Series, never>[]>(
    () => [
      {
        id: 'name',
        header: 'Series',
        meta: { primary: true },
        cell: ({ row }) => (
          <Link
            to="/platform/series/$seriesId"
            params={{ seriesId: row.original.id }}
            className="font-medium hover:underline"
          >
            {row.original.name}
          </Link>
        ),
      },
      {
        id: 'slug',
        header: 'Slug',
        cell: ({ row }) => <span className="font-mono text-xs">{row.original.slug}</span>,
      },
      {
        id: 'editions',
        header: 'Editions',
        meta: { align: 'end' },
        cell: ({ row }) => (
          <Link
            to="/platform/tenants"
            search={{ q: '', status: '', plan: '', series: row.original.slug }}
            className="tabular-nums hover:underline"
          >
            {row.original.editions}
          </Link>
        ),
      },
      {
        id: 'template',
        header: 'Template',
        cell: ({ row }) =>
          row.original.template === null ? (
            <span className="text-muted-foreground">None</span>
          ) : (
            row.original.template.name
          ),
      },
      ...(canManage
        ? [
            {
              id: 'actions',
              header: () => <span className="sr-only">Actions</span>,
              meta: { align: 'end' as const, hideOnCard: true },
              cell: ({ row }: { row: { original: Series } }) => (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setDialog({ editing: row.original })}
                  aria-label={`Rename ${row.original.name}`}
                >
                  <PencilIcon />
                  Rename
                </Button>
              ),
            } satisfies ColumnDef<Series, never>,
          ]
        : []),
    ],
    [canManage],
  )

  return (
    <>
      <PageHeader
        title="Series"
        description="A series is a brand or recurring event; each of its editions is a tenant."
        actions={
          canManage ? (
            <Button onClick={() => setDialog({ editing: null })}>
              <PlusIcon />
              New series
            </Button>
          ) : null
        }
      />
      <DataTable
        caption="Every event series"
        columns={columns}
        data={data.items}
        getRowId={(series) => series.id}
        empty={<EmptyState icon={<LayersIcon />} title="No series yet" />}
      />
      {dialog === null ? null : (
        <SeriesDialog
          key={dialog.editing?.id ?? 'new'}
          open
          onOpenChange={(open) => (open ? undefined : setDialog(null))}
          editing={dialog.editing}
        />
      )}
    </>
  )
}
